/**
 * Utility functions for calculating how many dropdown items can fit
 * in a given container width, with support for dynamic truncation
 * and "+ X more" pill display.
 */

/**
 * Estimates the width of text in pixels.
 * Uses a rough estimate of 8px per character for typical font sizes.
 */
export const estimateTextWidth = (text: string): number => {
  return text.length * 8
}

/**
 * Checks if all items can fit in the available width without needing a "+ X more" pill.
 * This is used to determine if we should use alphabetical sorting (when all fit)
 * or length-based sorting (when truncation is needed).
 */
export const doAllItemsFit = <T extends { name: string }>(
  items: T[],
  availableWidth: number,
): boolean => {
  if (items.length === 0) return true

  const commaWidth = 8 // ", " separator width
  let totalWidth = 0

  for (let i = 0; i < items.length; i++) {
    const itemWidth = estimateTextWidth(items[i].name)
    const separatorWidth = i > 0 ? commaWidth : 0
    totalWidth += itemWidth + separatorWidth
  }

  return totalWidth <= availableWidth
}

/**
 * Calculates how many items can fit in the available width,
 * accounting for the "+ X more" pill if there are remaining items.
 *
 * @param items - Array of items with a `name` property
 * @param availableWidth - Available width in pixels
 * @param pillWidth - Approximate width for "+ X more" pill (default: 80px)
 * @returns Object with `visible` items that fit and `remaining` items that don't
 */
export const calculateVisibleItems = <T extends { name: string }>(
  items: T[],
  availableWidth: number,
  pillWidth = 80, // Approximate width for "+ X more" pill
): { visible: T[]; remaining: T[] } => {
  if (items.length === 0) {
    return { visible: [], remaining: [] }
  }

  let usedWidth = 0
  const visible: T[] = []
  const commaWidth = 8 // ", " separator width

  for (let i = 0; i < items.length; i++) {
    const itemWidth = estimateTextWidth(items[i].name)
    const separatorWidth = i > 0 ? commaWidth : 0
    const totalItemWidth = itemWidth + separatorWidth

    // Check if we can fit this item plus the pill (if there are more items)
    const hasMore = i < items.length - 1
    const neededWidth =
      usedWidth + totalItemWidth + (hasMore ? pillWidth + commaWidth : 0)

    if (neededWidth <= availableWidth) {
      visible.push(items[i])
      usedWidth += totalItemWidth
    } else {
      break
    }
  }

  return {
    visible,
    remaining: items.slice(visible.length),
  }
}
